<script lang="ts">
  import { fetchStation } from "./services/station-service";
  import { SearchState } from "./services/search-service.js";
  import StationMiniMap from "./components/station/StationMiniMap.svelte";
  import type { Station } from "./models/station-detail.interface";

  let stationLoaded: Promise<Station>;
  let durationYears: number | null = null;

  let detailsOpen = true;
  let periodOpen  = true;

  function goBack() {
    if (document.referrer) {
      window.location.href = document.referrer;
    } else {
      history.back();
    }
  }

  function formatDisplayDate(dateStr: string): string {
    if (!dateStr || dateStr === "Unknown" || dateStr === "Present") return dateStr;
    const d = new Date(dateStr);
    if (isNaN(d.getTime()) || d.getFullYear() < 1800) return "Unknown";
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  }

  function calcDurationYears(beginDate: string, endDate: string): number | null {
    const start = beginDate === "Unknown" ? null : new Date(beginDate);
    const end   = (endDate === "Present" || endDate === "Unknown") ? new Date() : new Date(endDate);
    if (!start || isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 365.25));
  }

  SearchState.subscribe(search => {
    stationLoaded = loadStation(search?.params.get('stations'));
  });


  async function loadStation(id: string): Promise<Station> {
    if (!id) throw new Error("No station ID provided.");

    const station = await fetchStation(id);
    if (!station) throw new Error("Station not found.");

    durationYears = calcDurationYears(station.beginDate, station.endDate);

    return station;
  }
</script>

<div class="detail-page">
  {#await stationLoaded}
    <div class="state-message">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

  {:then station}
    <div class="detail-header">
      <button on:click={goBack} class="back-link" aria-label="Go back">
        <i class="bi bi-arrow-left"></i> Back
      </button>
      <h1 class="station-title">{station.name}</h1>
    </div>

    <div class="detail-body">
      <div class="detail-left">

        <!-- Station Details -->
        <div class="info-card">
          <button
            class="info-card-header"
            on:click={() => (detailsOpen = !detailsOpen)}
            aria-expanded={detailsOpen}
          >
            <span class="header-left">
              <i class="bi bi-info-circle-fill header-icon"></i>
              Station Details
            </span>
            <i class="bi bi-chevron-{detailsOpen ? 'up' : 'down'} chevron-icon"></i>
          </button>
          {#if detailsOpen}
            <div class="info-card-body">

              <div class="info-row">
                <span class="info-label">ID</span>
                <span class="info-value mono">{station.id}</span>
              </div>

              <div class="info-row">
                <span class="info-label">Coordinates</span>
                <span class="info-value mono">
                  {station.latitude.toFixed(4)}, {station.longitude.toFixed(4)}
                </span>
              </div>

              {#if station?.otherNames?.filter(f => f !== station.name).length > 0}
                <div class="info-row info-row-top">
                  <span class="info-label">Aliases</span>
                  <div class="id-badge-list">
                    {#each station.otherNames.filter(f => f !== station.name) as alias}
                      <span class="id-badge" title="Aliases: {alias}">
                        <span class="id-badge-type">{alias}</span>
                      </span>
                    {/each}
                  </div>
                </div>
              {/if}

              {#if station?.datasets?.length > 0}
                <div class="info-row info-row-top">
                  <span class="info-label">Datasets</span>
                  <div class="id-badge-list">
                    {#each station.datasets as dataset}
                      <span class="id-badge" title="{dataset.name}: {dataset.id}">
<!--                        TODO: Probably want to figure out a way to store links or dataset type in the station index-->
<!--                          * Should only apply to land/surface datasets so a simple approach like this is probably fine for now-->
<!--                          * Should also link station pre-selected but we need the secondary station indexes first-->
                        <a href="../../{dataset.id.endsWith('publication') ? 'publications' : 'datasets'}/{dataset.id}">{dataset.name}</a>
                      </span>
                    {/each}
                  </div>
                </div>
              {/if}

              {#if station?.identifiers?.length > 0}
                <div class="info-row info-row-top">
                  <span class="info-label">Identifiers</span>
                  <div class="id-badge-list">
                    {#each station.identifiers as identifier}
                      <span class="id-badge" title="{identifier.idType}: {identifier.id}">
                        <span class="id-badge-type">{identifier.idType}</span>
                        <span class="id-badge-value">{identifier.id}</span>
                      </span>
                    {/each}
                  </div>
                </div>
              {/if}

            </div>
          {/if}
        </div>

        <!-- Period of Record -->
        <div class="info-card">
          <button
            class="info-card-header"
            on:click={() => (periodOpen = !periodOpen)}
            aria-expanded={periodOpen}
          >
            <span class="header-left">
              <i class="bi bi-calendar-range-fill header-icon"></i>
              Period of Record
            </span>
            <i class="bi bi-chevron-{periodOpen ? 'up' : 'down'} chevron-icon"></i>
          </button>
          {#if periodOpen}
            <div class="info-card-body">

              <div class="info-row">
                <span class="info-label">Begin Date</span>
                <span class="info-value">{formatDisplayDate(station.beginDate)}</span>
              </div>

              <div class="info-row">
                <span class="info-label">End Date</span>
                <span class="info-value">
                  {#if station.endDate === "Present"}
                    <span class="badge-active">
                      <i class="bi bi-circle-fill active-dot"></i>
                      Active
                    </span>
                  {:else}
                    {formatDisplayDate(station.endDate)}
                  {/if}
                </span>
              </div>

              {#if durationYears !== null}
                <div class="info-row">
                  <span class="info-label">Duration</span>
                  <span class="info-value">
                    {durationYears} year{durationYears !== 1 ? "s" : ""} of record
                  </span>
                </div>
              {/if}

            </div>
          {/if}
        </div>

      </div>

      <!-- Station Location -->
      <div class="detail-right">
        <StationMiniMap lat={station.latitude} lng={station.longitude} />
      </div>

    </div>

    <div class="detail-bottom">
      <div class="bottom-placeholder">
        <i class="bi bi-bar-chart-line"></i>
        <p>Data plot coming soon</p>
      </div>
    </div>

  {:catch error}
    <div class="state-message">
      <div class="alert alert-warning">
        <i class="bi bi-exclamation-triangle me-2"></i>
        <strong>{error.message}</strong>
      </div>
    </div>
  {/await}
</div>

<style>
  .detail-page {
    min-height: 100vh;
    background-color: #f8f9fa;
    display: flex;
    flex-direction: column;
  }

  .detail-header {
    padding: 1rem 1.25rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .back-link {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .back-link:hover {
    color: #162e51;
  }

  .station-title {
    margin: 0;
    font-size: 1.6rem;
    font-weight: 700;
    color: #162e51;
    line-height: 1.2;
  }

  .detail-body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
    padding: 1.25rem;
  }

  .state-message {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem;
  }

  .detail-left {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .detail-right {
    display: flex;
    flex-direction: column;
  }

  .info-card {
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    overflow: hidden;
  }

  .info-card-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #162e51;
    color: white;
    padding: 0.6rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.15s ease;
  }

  .info-card-header:hover {
    background-color: #1e3d6e;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .header-icon {
    font-size: 0.9rem;
    opacity: 0.9;
  }

  .chevron-icon {
    font-size: 0.75rem;
    opacity: 0.7;
    flex-shrink: 0;
  }

  .info-card-body {
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
  }

  .info-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.55rem 0;
    border-bottom: 1px solid #f1f3f5;
  }

  .info-row:last-child {
    border-bottom: none;
  }

  .info-row-top {
    align-items: flex-start;
  }

  .info-label {
    width: 110px;
    flex-shrink: 0;
    font-size: 0.8rem;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .info-value {
    font-size: 0.9rem;
    color: #212529;
    flex: 1;
  }

  .info-value.mono {
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 0.825rem;
  }

  .badge-active {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background-color: #d1fae5;
    color: #065f46;
    border: 1px solid #6ee7b7;
    border-radius: 12px;
    padding: 2px 8px;
    font-size: 0.8rem;
    font-weight: 600;
  }

  .active-dot {
    font-size: 0.5rem;
    vertical-align: middle;
  }

  .id-badge-list {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    flex: 1;
  }

  .id-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 0.75rem;
  }

  .id-badge-type {
    color: #6c757d;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .id-badge-value {
    font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    color: #212529;
  }

  .detail-bottom {
    margin: 0 1.25rem 1.25rem;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    flex: 1;
    min-height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bottom-placeholder {
    text-align: center;
    color: #adb5bd;
  }

  .bottom-placeholder i {
    font-size: 2.5rem;
    display: block;
    margin-bottom: 0.5rem;
  }

  .bottom-placeholder p {
    margin: 0;
    font-size: 0.9rem;
  }

  @media (max-width: 900px) {
    .detail-body {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 480px) {
    .info-label {
      width: 90px;
    }
  }
</style>
